/**
 * The board's data seam, following growth/src/lib/data: screens ask a
 * BoardRepo and never import supabase-js. SupabaseBoardRepo is the real one,
 * and FakeBoardRepo backs the tests.
 */

import type { LinkableObjective } from "@/lib/priority-link";
import type { AssigneeLink, BoardSnapshot, Card, ColumnRow, Label, LabelColor, LabelLink } from "./types";

/** One row-level change pushed by Realtime. */
export type BoardChange =
  | { table: "hq_tasks"; type: "INSERT" | "UPDATE"; row: Card }
  | { table: "hq_tasks"; type: "DELETE"; id: string }
  | { table: "hq_task_columns"; type: "INSERT" | "UPDATE"; row: ColumnRow }
  | { table: "hq_task_columns"; type: "DELETE"; id: string }
  | { table: "hq_task_assignees"; type: "INSERT" | "DELETE"; row: AssigneeLink }
  | { table: "hq_task_labels"; type: "INSERT" | "UPDATE"; row: Label }
  | { table: "hq_task_labels"; type: "DELETE"; id: string }
  | { table: "hq_task_label_links"; type: "INSERT" | "DELETE"; row: LabelLink }
  | { table: "hq_board_members"; type: "INSERT" | "DELETE"; employee_id: string };

export interface BoardRepo {
  load(boardId: string): Promise<BoardSnapshot>;
  /** Active company priorities a card can link to. */
  listObjectives(): Promise<LinkableObjective[]>;

  createCard(input: { boardId: string; columnId: string; title: string; position: number; createdBy: string }): Promise<Card>;
  /**
   * `clearArchive` puts an archived card back on the board in the same write
   * as the move, when it is leaving the Complete column.
   */
  moveCard(cardId: string, columnId: string, position: number, opts?: { clearArchive?: boolean }): Promise<void>;
  /** Puts these people on a card, on top of anyone already on it. */
  addAssignees(cardId: string, employeeIds: string[]): Promise<void>;

  createLabel(boardId: string, name: string, color: LabelColor): Promise<Label>;
  setCardLabel(cardId: string, labelId: string, on: boolean): Promise<void>;

  addMember(boardId: string, employeeId: string): Promise<void>;
  /** Takes them off this board's cards too. Returns how many cards that was. */
  removeMember(boardId: string, employeeId: string): Promise<number>;

  /**
   * Live changes for one board. `onReconnect` fires after the socket drops
   * and comes back, because changes in the gap were missed and a full
   * reload is the honest answer.
   */
  subscribe(boardId: string, onChange: (c: BoardChange) => void, onReconnect: () => void): () => void;
}

const byPosition = <T extends { position: number }>(a: T, b: T) => Number(a.position) - Number(b.position);

function upsert<T extends { id: string }>(list: T[], row: T): T[] {
  const i = list.findIndex(x => x.id === row.id);
  if (i === -1) return [...list, row];
  const next = [...list];
  next[i] = { ...next[i], ...row };
  return next;
}

/**
 * Applies one Realtime change to the snapshot. Pure. Changes for other
 * boards (assignee and label-link events arrive unfiltered) are dropped.
 */
export function applyChange(s: BoardSnapshot, c: BoardChange): BoardSnapshot {
  switch (c.table) {
    case "hq_tasks":
      if (c.type === "DELETE") {
        return {
          ...s,
          cards: s.cards.filter(x => x.id !== c.id),
          assignees: s.assignees.filter(a => a.task_id !== c.id),
          labelLinks: s.labelLinks.filter(l => l.task_id !== c.id),
        };
      }
      if (c.row.board_id !== s.board.id) {
        // Moved to another board: it leaves this one.
        return { ...s, cards: s.cards.filter(x => x.id !== c.row.id) };
      }
      return { ...s, cards: upsert(s.cards, c.row) };

    case "hq_task_columns":
      if (c.type === "DELETE") return { ...s, columns: s.columns.filter(x => x.id !== c.id) };
      if (c.row.board_id !== s.board.id) return s;
      return { ...s, columns: upsert(s.columns, c.row).sort(byPosition) };

    case "hq_task_assignees": {
      if (!s.cards.some(x => x.id === c.row.task_id)) return s;
      const rest = s.assignees.filter(a => !(a.task_id === c.row.task_id && a.employee_id === c.row.employee_id));
      return { ...s, assignees: c.type === "INSERT" ? [...rest, c.row] : rest };
    }

    case "hq_task_labels":
      if (c.type === "DELETE") {
        return { ...s, labels: s.labels.filter(l => l.id !== c.id), labelLinks: s.labelLinks.filter(l => l.label_id !== c.id) };
      }
      if (c.row.board_id !== s.board.id) return s;
      return { ...s, labels: upsert(s.labels, c.row).sort(byPosition) };

    case "hq_task_label_links": {
      if (!s.cards.some(x => x.id === c.row.task_id)) return s;
      const rest = s.labelLinks.filter(l => !(l.task_id === c.row.task_id && l.label_id === c.row.label_id));
      return { ...s, labelLinks: c.type === "INSERT" ? [...rest, c.row] : rest };
    }

    case "hq_board_members": {
      const rest = s.memberIds.filter(id => id !== c.employee_id);
      const keep = c.type === "INSERT" || c.employee_id === s.board.owner_employee_id;
      return { ...s, memberIds: keep ? [...rest, c.employee_id] : rest };
    }
  }
}

/**
 * Where a card lands when dropped at `index` in a column: midway between
 * its new neighbours, so only the moved card is written.
 */
export function positionAt(columnCards: Card[], index: number, movingId: string): number {
  const others = columnCards.filter(c => c.id !== movingId).sort(byPosition);
  const before = others[index - 1];
  const after = others[index];
  if (before && after) return (Number(before.position) + Number(after.position)) / 2;
  if (before) return Number(before.position) + 1;
  if (after) return Number(after.position) - 1;
  return 0;
}
